import {Check, ReworkStatus} from '../../../../core/models/checks-model';
import {patchState, signalStore, withComputed, withHooks, withMethods, withState} from '@ngrx/signals';
import {computed, inject} from '@angular/core';
import {CheckService} from '../../../../core/checks/check';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {of, pipe} from 'rxjs';
import {switchMap, tap} from 'rxjs/operators';
import {Project} from '../../../../core/models/project-model';
import {ProjectService} from '../../../../core/projects/project';
import {tapResponse} from '@ngrx/operators';


type contextState = {
  checkId: string | null;
  projectCode: string | null;
  currentCheck: Check | null;
  isLoading: boolean;
  error: string | null;
  /** Wird von refreshCheck() hochgezählt, um einen Reload zu erzwingen. */
  refreshCounter: number;
}

const initialState : contextState = {
  checkId: null,
  currentCheck: null,
  projectCode: null,
  isLoading: false,
  error: null,
  refreshCounter: 0,
};

export const PendingCheckContextStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withComputed((
    store,
    projectService = inject(ProjectService)) => ({

    projectsById: computed(() =>
      new Map(projectService.projects().map(p => [p.contractNr, projectService.mapProject(p)]))
    ),

    checkInfoViewModel: computed(() => {
      const check = store.currentCheck();
      return check ? { ...check } : null;
    }),

  })),

  withMethods((
    store,
    checkService = inject(CheckService)
  ) => ({

    /**
     * Setzt nur checkId/projectCode. Das eigentliche Laden übernimmt der
     * reaktive Effect in withHooks – der reagiert zusätzlich auf projectsById,
     * damit kein Race-Condition entsteht, falls die Projektliste noch nicht
     * geladen ist (siehe onInit unten).
     */
    setCheckContext(checkId: string | null, projectCode: string | null) {
      patchState(store, { checkId, projectCode });
    },

    /**
     * Erzwingt einen Reload des aktuellen Checks (z. B. nach Stage-/Status-Updates),
     * ohne checkId/projectCode zu ändern. Der Trigger in withHooks reagiert auf
     * refreshCounter, da checkId/projectCode dabei unverändert bleiben.
     */
    refreshCheck() {
      patchState(store, { refreshCounter: store.refreshCounter() + 1 });
    },

    patchReworkStatusLocally(reworkStatus: ReworkStatus) {
      const current = store.currentCheck();
      if (!current) return;
      patchState(store, { currentCheck: { ...current, reworkStatus } });
    },

    loadCurrentCheck: rxMethod<{
      checkId: string | null;
      projectCode: string | null;
      projects: Map<string, Project>;
    }>(
      pipe(
        tap(() => patchState(store, { isLoading: true })),
        switchMap(({ checkId, projectCode, projects }) => {
          if (!checkId || !projectCode) {
            patchState(store, { currentCheck: null, isLoading: false });
            return of(null);
          }

          // Projektliste noch nicht geladen → weiter warten (Ladezustand halten,
          // NICHT currentCheck auf null zurücksetzen → verhindert Flicker).
          if (projects.size === 0) {
            patchState(store, { isLoading: true });
            return of(null);
          }

          return checkService.loadCheckWithStages(checkId, projectCode, projects).pipe(
            tapResponse({
              next: (check) => patchState(store, { currentCheck: check, isLoading: false }),
              error: (err: any) => {
                patchState(store, { error: err.message, isLoading: false });
                console.error('Fehler beim Laden des Checks: ', err);
              }
            })
          )
        })
      )
    )
  })),

  // Reagiert reaktiv auf checkId/projectCode UND auf projectsById.
  // Verhindert die Race-Condition: setCheckContext() kann aufgerufen werden,
  // bevor die Projektliste (asynchron via ProjectService) geladen ist.
  withHooks({
    onInit(store) {
      const trigger = computed(() => ({
        checkId: store.checkId(),
        projectCode: store.projectCode(),
        projects: store.projectsById(),
        // Nicht Teil des Payloads für loadCurrentCheck, aber Bestandteil des
        // computed()-Trackings: Änderung von refreshCounter triggert reload.
        _refresh: store.refreshCounter(),
      }));
      store.loadCurrentCheck(trigger);
    }
  })
)
